import { useCallback, useRef, useState } from "react";

import { isPerspectiveAuthError } from "./auth_errors";

/** The slice of PerspectiveApiService the retry needs (kept structural so
 *  this file never imports the Apollo/graphql-ws client). */
type AuthRecoverable = { recoverAuth: () => Promise<boolean> };

type RecoverOptions = {
  /** True once the subscribing effect has been torn down (popover closed or
   *  re-anchored). */
  isCancelled: () => boolean;
  /** Recovery could not help — surface this message as the failure. */
  onGiveUp: (message: string) => void;
};

/**
 * One-shot auth recovery for cell-detail popovers (SUNGLAS-39).
 *
 * A subscription rejected for auth means the socket's token went stale.
 * `recoverFrom` refreshes the token / reopens the socket and then bumps
 * `retryNonce`; put the nonce in the fetch effect's deps to resubscribe.
 *
 * The guard is per KEY (a cell), not a single slot:
 * - each cell gets one attempt, so alternating between two failing cells
 *   cannot trigger recovery without bound;
 * - `markSucceeded` re-arms a cell once it loads, so a later token expiry on
 *   the same cell can recover again;
 * - a recovery whose popover was closed meanwhile does not spend the attempt.
 */
export function usePerspectiveAuthRetry(service: AuthRecoverable) {
  const attempted = useRef<Set<string>>(new Set());
  const [retryNonce, setRetryNonce] = useState(0);

  const recoverFrom = useCallback(
    (key: string, message: string, options: RecoverOptions): boolean => {
      if (!isPerspectiveAuthError(message) || attempted.current.has(key)) {
        return false;
      }
      attempted.current.add(key);
      service
        .recoverAuth()
        .then((canRetry) => {
          if (options.isCancelled()) {
            attempted.current.delete(key);
            return;
          }
          if (!canRetry) {
            // No fresher token to offer: a retry would be rejected the same
            // way, so say so instead of spinning.
            options.onGiveUp(message);
            return;
          }
          setRetryNonce((n) => n + 1);
        })
        .catch((e: unknown) => {
          if (!options.isCancelled()) {
            options.onGiveUp(String((e as Error)?.message || e));
          }
        });
      return true;
    },
    [service],
  );

  const markSucceeded = useCallback((key: string) => {
    attempted.current.delete(key);
  }, []);

  return { retryNonce, recoverFrom, markSucceeded };
}
